/**
 * MergeSOPsDialog
 *
 * Confirms the final name and starts the merge — POST /sops/merge-batch validates and flags every
 * selected SOP `mergeStatus: 'merging'`, then returns quickly (before the actual N-1 sequential
 * merges, each an LLM synthesis + S3 write on the backend, run in the background on the server).
 * The dialog awaits only that quick start: on success it closes and exits selection; a validation
 * failure (e.g. mismatched categories, a SOP already mid-merge) surfaces here so it can be fixed
 * without losing the selection. Ongoing progress and outcome show as a badge on the affected rows
 * (mergeStatus/mergeError, read fresh from the server — survives a refresh or closed tab).
 */

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from '@/components/ui/sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useMergeSOPs } from '@/hooks/use-sops';
import { getApiErrorMessage } from '@/lib/utils';
import type { SOP } from '@/types';

const mergeSchema = z.object({
  finalName: z.string().min(1, 'Name is required').max(200, 'Name is too long')
});
type MergeFormData = z.infer<typeof mergeSchema>;

interface MergeSOPsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The selected SOPs, in selection order. sops[0] stays canonical and absorbs the rest — the
   *  list below shows that ordering so the operator can see which one it is before confirming. */
  sops: SOP[];
  onMerged?: () => void;
}

function humanizeIntent(intent: string): string {
  return intent.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function MergeSOPsDialog({ open, onOpenChange, sops, onMerged }: MergeSOPsDialogProps) {
  const mergeMutation = useMergeSOPs();

  const form = useForm<MergeFormData>({
    resolver: zodResolver(mergeSchema),
    defaultValues: { finalName: '' }
  });

  useEffect(() => {
    if (open) form.reset({ finalName: sops[0] ? humanizeIntent(sops[0].intent) : '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onSubmit = async (data: MergeFormData) => {
    const finalName = data.finalName.trim();
    const count = sops.length;
    const sopIds = sops.map((s) => s.id);

    try {
      // Awaited, but this only covers the quick validate-and-flag step on the server — the
      // actual merge work continues in the background after this resolves. A rejection here is
      // a real validation failure (mismatched categories, one already mid-merge, etc.), so the
      // dialog stays open with the selection intact rather than closing on a request that never
      // started anything.
      await mergeMutation.mutateAsync({ sopIds, finalName });
      toast.success(`Merging ${count} drafts into "${finalName}" — watch the list for progress`);
      onOpenChange(false);
      onMerged?.();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to start the merge'));
    }
  };

  const canMerge = sops.length >= 2;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='w-lg p-4'>
        <DialogHeader>
          <DialogTitle className='text-sm font-semibold'>Merge {sops.length} drafts</DialogTitle>
          <DialogDescription className='text-xs'>
            These drafts will be combined into one, keeping every step from all of them. The others will be
            deprecated with a redirect to the merged draft.
          </DialogDescription>
        </DialogHeader>

        {!canMerge ? (
          <p className='text-xs text-muted-foreground'>Select at least two drafts to merge.</p>
        ) : (
          <ul className='max-h-40 overflow-y-auto rounded-md border border-gray-200 text-xs divide-y'>
            {sops.map((sop, index) => (
              <li key={sop.id} className='flex items-center justify-between gap-2 px-3 py-2 capitalize'>
                <span>{humanizeIntent(sop.intent)}</span>
                {index === 0 && (
                  <span className='shrink-0 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-medium normal-case text-indigo-600'>
                    kept, others merge in
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='flex flex-col gap-4'>
            <FormField
              control={form.control}
              name='finalName'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className='text-xs font-medium text-gray-700'>Final draft name</FormLabel>
                  <FormControl>
                    <Input className='text-xs h-10' placeholder='e.g. Refund Request' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className='mt-2'>
              <Button
                type='button'
                variant='outline'
                size='sm'
                className='text-xs'
                onClick={() => onOpenChange(false)}
                disabled={mergeMutation.isPending}
              >
                Cancel
              </Button>
              <Button type='submit' size='sm' className='text-xs' disabled={!canMerge || mergeMutation.isPending}>
                {mergeMutation.isPending ? 'Starting…' : `Merge ${sops.length} drafts`}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
